import React from "react";
import { Link } from "react-router-dom";

const FAQS = [
  {
    q: "What is performance calibration?",
    a: "Performance calibration is the structured process of comparing how an employee and their manager rate the same set of role responsibilities. The point isn't a score — it's surfacing the perception gaps so the conversation that follows is grounded in evidence, not impressions.",
  },
  {
    q: "Do my employees need to create an account?",
    a: "No. CALIBRATE issues a cryptographically-signed, single-use magic-link per calibration. Your employee clicks the link, completes their self-assessment in private, and submits. No password, no inbox sprawl, and the link is tied to that one calibration only.",
  },
  {
    q: "How does the blind-rating workflow remove bias?",
    a: "Both parties rate independently. The manager cannot see the employee's scores while rating, and vice-versa. Only when both have submitted does the side-by-side comparison overlay unlock for the 1:1 conversation. This means neither side is anchored to the other's ratings.",
  },
  {
    q: "What does the AI actually do?",
    a: "Three things. First, our PD Generator can draft a full Position Description from scratch — you provide a role title, department, and answer 4-5 targeted discovery questions, and Claude 3.5 Sonnet composes a ~800-word PD covering responsibilities, KPIs, required skills, and stakeholder relationships in about 30 seconds. Second, when you upload an existing PD PDF, the AI extracts 6-15 specific, evaluative tasks (no boilerplate, no benefits text). Third, after both ratings are submitted, it analyses the largest gaps and proposes 3-5 neutral conversation starters for the manager to use in the calibration meeting.",
  },
  {
    q: "How does the PD Generator work?",
    a: "The PD Generator is a two-step AI flow. Step 1: tell us the role — job title and department — and Claude generates 4-5 targeted discovery questions specific to that role (not generic HR boilerplate). Step 2: answer those questions with a few keywords each (bulleted lists are fine — the AI expands them), and Claude drafts the full Position Description as clean Markdown. You can copy it, download it, or feed it straight into a new employee profile to start a calibration.",
  },
  {
    q: "How many PDs can I generate?",
    a: "The Free plan includes 1 lifetime PD generation so you can try the feature before subscribing. Every paid plan includes a fresh quota that equals your employee-profile allowance — Small Business: 25 PDs/year, Growth: 100 PDs/year, Enterprise: up to 10,000 PDs/year. The counter resets automatically on your organisation's join anniversary. You can see your remaining quota and reset date directly on the PD Generator page.",
  },
  {
    q: "Is my data isolated from other organizations?",
    a: "Yes. CALIBRATE enforces strict per-organization data isolation at the application layer. Every profile, calibration, rating, and audit log carries an org_id and is filtered on every query. Org Admins can only see their own organization; Platform Owners can govern plans, but cannot read individual ratings.",
  },
  {
    q: "What does it cost?",
    a: "The Free plan is free forever — no credit card required — and includes 1 PD generation for you to try the AI. Paid plans start at A$29/month (Small Business, 25 PDs/year included) and A$99/month (Growth, 100 PDs/year included), with volume pricing available for Enterprise. Check the Pricing page for current seat, profile, and PD-quota limits.",
  },
];

export default function FAQ() {
  // Inject FAQPage schema dynamically so Google can surface answers in SERPs.
  React.useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    });
    script.id = "faq-jsonld";
    document.head.appendChild(script);
    document.title = "FAQ — CALIBRATE | Performance calibration software, Australia";
    return () => { script.remove(); };
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="faq-page">
      <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#666] mb-4">
        [ FAQ ]
      </div>
      <h1 className="font-display font-bold text-5xl sm:text-6xl tracking-tight mb-4">
        Frequently asked questions
      </h1>
      <p className="text-[#666] text-lg leading-relaxed mb-12 max-w-2xl">
        Quick answers about how CALIBRATE works, what it costs, and how it keeps your
        organisation's data isolated. Can't find what you're looking for?
        {" "}
        <a href="mailto:support@calibrate.management" className="underline hover:text-[#FF4F00]">
          Email us
        </a>.
      </p>

      <div className="space-y-10" data-testid="faq-list">
        {FAQS.map(({ q, a }, idx) => (
          <article key={q} className="border-b border-[#111]/10 pb-8 last:border-0" data-testid={`faq-item-${idx}`}>
            <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mb-2">
              {String(idx + 1).padStart(2, "0")}
            </div>
            <h2 className="font-display font-bold text-2xl mb-3 leading-snug">{q}</h2>
            <p className="text-[#444] leading-relaxed">{a}</p>
          </article>
        ))}
      </div>

      <div className="mt-16 p-8 cal-card flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-1">[ STILL CURIOUS? ]</div>
          <div className="font-display font-bold text-xl">Try CALIBRATE free — no credit card</div>
        </div>
        <Link to="/register" className="cal-btn" data-testid="faq-cta-register">
          Create account →
        </Link>
      </div>

      <footer className="mt-16 pt-8 border-t border-[#111]/10 text-xs font-mono-cal text-[#666] flex flex-wrap gap-5">
        <Link to="/" className="hover:text-[#FF4F00]" data-testid="faq-back-home">← Home</Link>
        <Link to="/pricing" className="hover:text-[#FF4F00]">Pricing</Link>
        <Link to="/blog" className="hover:text-[#FF4F00]">Blog</Link>
        <Link to="/terms" className="hover:text-[#FF4F00]">Terms</Link>
        <a href="mailto:support@calibrate.management" className="hover:text-[#FF4F00]">Contact</a>
      </footer>
    </div>
  );
}
